import type { VoidFunctionComponent } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import { useReportType } from '../hooks';
import { isReleaseGroupScope, type ReportsV3Params, type ReportType } from '../types';

import { reportHeadingsMap } from './common';

import styles from './Heading.module.less';

/**
 * Helper so that we don't need to do the same tedious string interpolation for the scope label.
 * @param {boolean} isReleaseGroup - Whether the scope is a release group.
 * @returns {'project' | 'release group'} The label for the scope.
 */
const labelForScope = (isReleaseGroup: boolean): 'project' | 'release group' =>
  isReleaseGroup ? 'release group' : 'project';

type GetDescriptionFn = (scopeLabel: 'project' | 'release group') => string;

const headingsMap: Readonly<Record<ReportType, GetDescriptionFn>> = {
  licensing: scopeLabel =>
    `Summarizes the licenses used across your ${scopeLabel}'s dependencies to help you stay compliant and audit-ready.`,
  sbom: scopeLabel =>
    `A detailed inventory of all open-source and third-party components used in your ${scopeLabel}, including their versions, licenses, and relationships.`,
  vulnerability: scopeLabel =>
    `Generate a vulnerability report for your ${scopeLabel}.`,
  remediationGuidance: scopeLabel =>
    `Generate a remediation guidance plan for your ${scopeLabel}.`,
};

/**
 * A component that displays the heading and description for the report.
 */
export const Heading: VoidFunctionComponent = () => {
  const params = useParams<ReportsV3Params>();
  const reportType = useReportType();
  const isReleaseGroup = isReleaseGroupScope(params);
  const scopeLabel = labelForScope(isReleaseGroup);

  return (
    <header className={styles.header}>
      <h1 className={styles.heading}>
        {reportHeadingsMap[reportType] ?? 'Report'}
      </h1>
      <p className={styles.description}>
        {headingsMap[reportType]?.(scopeLabel) ?? `Generate a report for your ${scopeLabel}.`}
      </p>
    </header>
  );
};
